import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { messageService } from '../service/messageService';

import jwt from 'jwt-decode';
import { useAuth } from '../hooks/useAuth';
import handleLoginFromAuth from '../hooks/loginHook';
import { useAppDispatch } from '../store';
import { FlowStage, flowActions } from '../store/flow';
import styles from '../assets/CreatePassword.module.css';
const {
  USER_INFORMATION_URL,
  PASSWORD_REGEX,
  ERROR_URL,
  VERIFY_TOKEN_API_URL,
  SET_PASSWORD_API_URL,
  UPDATE_PASSWORD_CREATION_STATUS_API_URL,
  TOKEN_NAME,
  AOF_URL,
} = require('../helper/application-conf').default;
import MessageBanner from './MessageBanner';
import axios from '../hooks/axios';
import useAxiosBuyer from '../hooks/useAxiosBuyer';
import { StorageService } from '../helper/storage-service';
import { authorizedOfficerActions } from '../store/authorizedOfficer';
import { companyActions } from '../store/company';
import { userActions } from '../store/user';
import { mapApiDataToStores } from '../helper/dataMapper';

const {
  PasswordInput, Label, dlsGray03, GlyphCheck, GlyphClose, ButtonPrimary, dlsRed, dlsGreen, Alert, DialogBox, DialogBoxHeader, DialogBoxBody,
} = require('@americanexpress/dls-react');

interface CreatePasswordProps {
  userID: string,
  siteName: string,
  token: string,
  enrollment: string
}

const CreatePassword = (props: CreatePasswordProps) => {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordStatus, setPasswordStatus] = useState('default');
  const [passwordLengthCheck, setPasswordLengthCheck] = useState('default');
  const [errorMessage, setErrorMessage] = useState(t('genericError'));
  const [passwordCharacterCheck, setPasswordCharacterCheck] = useState('default');

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [enrollmentFlag, setEnrollmentFlag] = useState(false);
  const { setAuth } = useAuth() as any;
  const axiosBuyer = useAxiosBuyer();
  const storage = new StorageService();
  const location = useLocation();
  const emailId = location.state?.data.userName;
  const verifyPhone = location.state?.data
  const tokenNo = location.state?.data.token;

  const {
    siteName,
    userID,
    token,
    enrollment
  } = props;

  const showError = (flag: boolean) => {
    setCount((c) => c + 1);
    setHasError(flag);
  };

  const enrollmentFlagHandler = () => {
    if(enrollment == '') {
      setEnrollmentFlag(true);
    } else {
      setEnrollmentFlag(false);
    }
  };

  const [userName, setUserName] = useState('');

  const loadUserData = async () => {
    await axios.get(`${VERIFY_TOKEN_API_URL}${token}&userId=${userID}&sitename=${siteName}`).then((response) => {
      setUserName(response.data.data.email);
    }, () => {
      const errorText = {message: t('errors.email_expire'), status: "Expired"};
      navigate(ERROR_URL, { state: { errorText} });
    });
  };

  useEffect(() => {
    if (verifyPhone && verifyPhone.isPhoneVerified) {
      messageService.sendstepperToggle(false);
      setUserName(emailId);
    } else {
      appDispatch(flowActions.setStage(FlowStage.CreatePassword));
      loadUserData();
      enrollmentFlagHandler();
    }
  }, [appDispatch]);

  const confirmPassword = useCallback((passwordConf: string) => {
    if (password === passwordConf) {
      setPasswordStatus('success');
      return true;
    }
    setPasswordStatus('error');
    return false;
  }, [password]);

  const validatePassword = useCallback(() => {
    if (confirmPassword(passwordConfirmation) === true && passwordLengthCheck === 'success' && passwordCharacterCheck === 'success') {
      return true;
    }
    return false;
  }, [passwordLengthCheck, passwordCharacterCheck, confirmPassword, passwordConfirmation]);

  const checkPasswordLength = useCallback((pswd: string) => {
    if (pswd.length < 8) {
      setPasswordLengthCheck('error');
    } else {
      setPasswordLengthCheck('success');
    }
  }, []);

  const checkPasswordCharacters = useCallback((pswd: string) => {
    const regex = PASSWORD_REGEX;
    if (!pswd.match(regex)) {
      setPasswordCharacterCheck('error');
    } else {
      setPasswordCharacterCheck('success');
    }
  }, []);

  const setCreatedPasswordStatus = useCallback(async () => {
    await axiosBuyer.put(`${UPDATE_PASSWORD_CREATION_STATUS_API_URL}${siteName}`, null).then(() => {
      setTimeout(() => {
        getBuyerInfo();
      }, 1);
    }, () => {
      navigate(ERROR_URL);
    });
  }, [axiosBuyer, navigate]);

  const getBuyerInfo = () => {
    axiosBuyer.get(`${AOF_URL}${siteName}`
    ).then((getResponse) => {
      const [userInfo, authorizedOfficerInfo, companyInfo] = mapApiDataToStores(getResponse);
      const userStore: any = userInfo;
      const authorizedOfficerStore: any = authorizedOfficerInfo;
      const companyInfoStore: any = companyInfo;
      appDispatch(userActions.setUser(userStore));
      appDispatch(authorizedOfficerActions.setAuthorizedOfficer(authorizedOfficerStore));
      appDispatch(companyActions.setCompany(companyInfoStore));
      navigate(USER_INFORMATION_URL);
    }, (error) => {
      if (error.response && error.response.status === 400 && error.response.data) {
        const errorText = {message: error.response.data.errors[0].message};
        navigate(ERROR_URL, { state: { errorText } });
      } else {
        navigate(ERROR_URL);
      }
    });
  };

  const performLogin = useCallback(async () => {
    const bodyFormData = new URLSearchParams();
    bodyFormData.append('customer', siteName);
    bodyFormData.append('username', userName);
    bodyFormData.append('password', password);
    if (userName && password) {
      handleLoginFromAuth(siteName, userName, password).then((response: any) => {
        setSuccessModalOpen(true);
        showError(false);
        const accessToken = response?.data?.data?.accessToken;
        const refreshToken = response?.data?.data?.refreshToken;
        const decodedToken: any = jwt(accessToken);
        const role = {
          userId: decodedToken?.sub, emailId: decodedToken?.email, firstName: decodedToken?.given_name, lastName: decodedToken?.family_name, roleNames: ['ADMINISTRATOR'], originalRole: JSON.parse(decodedToken?.origin_customer_role),
        };
        setAuth({
          userName, password, accessToken, refreshToken, role,
        });
        storage.setSession(TOKEN_NAME, JSON.stringify(response.data.data));
      }, () => {
        showError(true);
      });
    }
  }, [password, siteName, userName]);

  const save = useCallback(async () => {
    let isPhoneVerified : any = false;
    let verifyPayload : any;

    const payload =  {
      siteName,
      userId: userID,
      password,
      token,
    }

    if(verifyPhone != null) {
      
      isPhoneVerified = verifyPhone.isPhoneVerified;

      verifyPayload =  {
        siteName: verifyPhone.site,
        userId: verifyPhone.userId,
        password,
        token: tokenNo,
      }
    }
     
    if (validatePassword()) {
      await axios.post(
        `${SET_PASSWORD_API_URL}?sendNotification=${enrollmentFlag}&isDAO=true`, 
        isPhoneVerified ? verifyPayload : payload,
        {
          headers: { 'Content-Type': 'application/json' },
        }).then((response) => {
          if (response.status === 200) {
            if(!isPhoneVerified){
              performLogin();
            }else{
              setSuccessModalOpen(true);
            }
          }
        }).catch(({ response }) => {
          setErrorMessage(response.data.value);
          showError(true);
        });
    }
  }, [validatePassword, password, siteName, userID, performLogin, token]);

  const next = useCallback(() => {
    let isPhoneVerified : any = false;

    if(verifyPhone != null) {
      isPhoneVerified =verifyPhone.isPhoneVerified 
    }

    if(!isPhoneVerified){
      setCreatedPasswordStatus();
    }else{
      window.location.pathname = `/${siteName}`;
    }
  }, []);

  return (
    <div className="container">
      <div className="row pad-4-t">
        <div className="col-md-offset-4 col-md-4">
          <h5 className="heading-5">{t('create_password.header')}</h5>
        </div>
      </div>
      <span key={count}>
        <MessageBanner type="warning" messageText={errorMessage || ''} showMessage={hasError} />
      </span>
      <div className="row pad-3-t">
        <div className="col-md-offset-4 col-md-4">
          <p className="body-3"><span className="label-3">{t('create_password.user_id')}</span> <span>{userName}</span></p>
        </div>
      </div>
      <div className="row pad-3-t">
        <div className="col-md-offset-4 col-md-4">
          <Label id="password-input-label" htmlFor="password-input">{t('create_password.new_password')}</Label>
          <PasswordInput
            id="password-input"
            status={passwordStatus}
            value={password}
            onChange={(event: any) => {
              setPassword(event.target.value);
              checkPasswordLength(event.target.value);
              checkPasswordCharacters(event.target.value);
            }}
          />
        </div>
      </div>
      <div className="row pad-t">
        <div className="col-md-offset-4 col-md-4">
          <Label id="confirm-password-input-label" htmlFor="confirm-password-input">{t('create_password.confirm_password')}</Label>
          <PasswordInput
            id="confirm-password-input"
            status={passwordStatus}
            value={passwordConfirmation}
            onChange={(event: any) => {
              setPasswordConfirmation(event.target.value);
              confirmPassword(event.target.value);
            }}
          />
          {passwordStatus === 'error'
            && <Alert status="error" className="pad-1-t">{t('errors.password_confirmation')}</Alert>}
        </div>

      </div>
      <div className="col-md-offset-4 col-md-4 pad-t">
        <p>{t('create_password.password_requirements_description')}</p>
        <div className="flex flex-start">
          {passwordLengthCheck === 'default' && <GlyphCheck className={`${styles.glyphOpticalAdjustment} pad-r`} theme={dlsGray03} />}
          {passwordLengthCheck === 'success' && <GlyphCheck className={`${styles.glyphOpticalAdjustment} pad-r`} theme={dlsGreen} />}
          {passwordLengthCheck === 'error' && <GlyphClose className={`${styles.glyphOpticalAdjustment} pad-r`} theme={dlsRed} />}
          <p className="body-1">{t('create_password.password_requirements_1')}</p>
        </div>
        <div className="flex flex-start">
          {passwordCharacterCheck === 'default' && <GlyphCheck className={`${styles.glyphOpticalAdjustment} pad-r`} theme={dlsGray03} />}
          {passwordCharacterCheck === 'success' && <GlyphCheck className={`${styles.glyphOpticalAdjustment} pad-r`} theme={dlsGreen} />}
          {passwordCharacterCheck === 'error' && <GlyphClose className={`${styles.glyphOpticalAdjustment} pad-r`} theme={dlsRed} />}
          <p className="body-1">{t('create_password.password_requirements_2')}</p>
        </div>
      </div>
      <div className="col-md-offset-5 col-md-2 pad-4-tb">
        <div className="flex flex-align-center">
          <ButtonPrimary size="md" onClick={save} id="create-password-button">{t('create')}</ButtonPrimary>
        </div>
      </div>
      {successModalOpen
        && (
          <DialogBox onClose={() => { setSuccessModalOpen(false); }} id="success-modal">
            <DialogBoxHeader id="success-modal-header">
              <p>{t('create_password.modal.header')}</p>
            </DialogBoxHeader>
            <DialogBoxBody id="success-modal-body">
              <p>{t('create_password.modal.body')}</p>
              <ButtonPrimary size="md" onClick={next} id="continue-button" className="margin-auto-lr margin-1-b margin-3-t">{t('continue')}</ButtonPrimary>
            </DialogBoxBody>
          </DialogBox>
        )}
    </div>
  );
};

export default CreatePassword;
